import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService, SelectItem } from 'primeng/api';
import { AuthGateway } from 'src/app/application/gateway/AuthGateway';
import { ExportOfferService } from 'src/app/application/service/ExportOfferService';
import { ExportOffer, ExportOfferInformation } from 'src/app/domain/models/ExportOffer';
import { Species } from 'src/app/domain/models/Species';
import { BasicUser } from 'src/app/domain/models/User';
import { Routes } from 'src/app/infrastructure/routes';
import { SpeciesUtils } from 'src/app/ui/util/species-utils';
import * as moment from 'moment';

@Component({
  selector: 'app-admin-manage-export-offers-page',
  templateUrl: './admin-manage-export-offers-page.component.html',
  styleUrls: ['./admin-manage-export-offers-page.component.css'],
  providers: [ConfirmationService]
})
export class AdminManageExportOffersPageComponent implements OnInit {

  exportOffers: ExportOfferInformation[] = []
  filteredExportOffers: ExportOfferInformation[] = []
  loading: boolean = false

  date: Date | undefined;

  saleType: string = '';
  isClosedSale: boolean = false;

  filterForm: FormGroup = this._formBuilder.group({
    offerId: [''],
    exporter: [''],
    specie: [''],
    variety: [''],
    loadDate: ['']
  })

  offersArray: number[] = []
  exportersArray: string[] = []
  speciesArray: string[] = []
  varietiesArray: string[] = []

  offersFilter: SelectItem[] = []
  exportersFilter: SelectItem[] = []
  speciesFilter: SelectItem[] = []
  varietiesFilter: SelectItem[] = []
  searchTerm: string = '';

  user: BasicUser | undefined

  constructor(
    private readonly _exportOfferService: ExportOfferService,
    private readonly _router: Router,
    private readonly _messageService: MessageService,
    private readonly translate: TranslateService,
    private readonly _confirmationService: ConfirmationService,
    private readonly _route: ActivatedRoute,
    private readonly _formBuilder: FormBuilder,
    private readonly _authService: AuthGateway
  ) {}

  ngOnInit(): void {
    this.user = this._authService.getUser()
    this.saleType = this._route.snapshot.data['saleType']
    this.isClosedSale = this.saleType === 'program'
    this.getExportOffers()
  }

  @ViewChild("dt1") dataTableComponent: any;
  setup(value: any, id: any){
    this.dataTableComponent.filters[id][0].value;
   if(value != null)
     this.dataTableComponent.filters[id][0].value = this.date;
 }

  getExportOffers(){
    this.loading = true;
    this.exportOffers = []
    this._exportOfferService.getAll().subscribe({
      next: (v) => {
        this.clearSelectFilters()
        const data = v.filter((el) => el.exportOffer.isClosedSale === this.isClosedSale)
        data.forEach(el => {
          this.setFilters(el)
        })

        this.sortFiters()
        this.exportOffers = data
        this.filteredExportOffers = data
        this.onFilter()
        this.loading = false
      },
      error: (e) => {
        this._messageService.add({ severity: 'error', summary: 'Acción fallida', detail: 'No se pudieron recuperar las ofertas.' });
      },
      complete: () => {
        this.loading = false
      }
    })
  }

  @ViewChild('dt1') dataTable: any
  filterGlobal(event: any) {
    this.dataTable.filterGlobal(event.target.value, 'contains')
  }

  goToAdminEdit(exportOfferId: number){
    this._router.navigate(['/', Routes.adminExportOfferEdit, exportOfferId], {state: {saleType: this.saleType}})
  }

  desactivate (exportOffer: ExportOffer){
    this._exportOfferService.desactive(exportOffer.id).subscribe(
      {
        next: () => {
          this._messageService.add({
            severity: 'warn',
            summary: this.translate.instant('AdminExportOffer.Messages.OfferDeactivated')
          });
          exportOffer.isActive = false;
        },
        error: (e) => {
          this._messageService.add({
            severity: 'error',
            summary: this.translate.instant('AdminExportOffer.Messages.ErrorDeactivatingOffer')
          });
        },
        complete: () => {
          this.loading = false
        }
      }
    )
  }

  activate (exportOffer: ExportOffer){
    this._exportOfferService.active(exportOffer.id).subscribe(
      {
        next: () => {
          this._messageService.add({
            severity: 'success',
            summary: this.translate.instant('AdminExportOffer.Messages.OfferActivated')
          });
          exportOffer.isActive = true;
        },
        error: (e) => {
          this._messageService.add({
            severity: 'error',
            summary: this.translate.instant('AdminExportOffer.Messages.ErrorActivatingOffer')
          });
        },
        complete: () => {
          this.loading = false
        }
      }
    )
  }

  deleteOffer (exportOffer: ExportOffer){
    const specieName = this.getSpeciesName(exportOffer.variety.species) + ' ' + exportOffer.variety.name
    this._confirmationService.confirm({
      message: this.translate.instant('AdminExportOffer.Messages.RemoveDetailConfirmDialogMessage', {value: specieName}),
      header: this.translate.instant('AdminExportOffer.Messages.RemoveDetailConfirmDialogHeader'),
      icon: 'pi pi-trash',
      acceptLabel: this.translate.instant('AdminExportOffer.Messages.DeleteButton'),
      rejectLabel: this.translate.instant('AdminExportOffer.Messages.CancelButton'),
      acceptIcon: 'pi',
      rejectIcon: 'pi',
      accept: () => {
        this.loading = true
        this._exportOfferService.deleteOffer(exportOffer.guid).subscribe(
          {
            next: () => {
              this._messageService.add({
                severity: 'success',
                summary: this.translate.instant('AdminExportOffer.Messages.OfferDeletedTitle'),
                detail: this.translate.instant('AdminExportOffer.Messages.OfferDeletedDetail')
              });
              this.getExportOffers()
            },
            error: (e) => {
              let errorMessage = ''
              const lang = this.translate.currentLang
              switch(lang) {
                case 'es':
                  errorMessage = e.error.messageEs
                  break
                case 'en':
                  errorMessage = e.error.messageEn
                  break
                default:
                  errorMessage = e.error.messageEn
              }
              if(e.status === 500){
                this._messageService.add({ severity: 'error', summary: this.translate.instant('MessageService.Summary.FailedAction'), detail: "No fue posible eliminar el registro, consulte con sistemas" })
              }
              else {
                this._messageService.add({ severity: 'error', summary: this.translate.instant('MessageService.Summary.FailedAction'), detail: errorMessage });
              }
              this.loading = false
            },
            complete: () => {
              this.loading = false
            }
          }
        )
      },
      reject: (type: any) => {}
    })
  }

  getSpeciesName(data: Species): string {
    if (this.user) {
      return SpeciesUtils.getSpeciesName(this.user.lang, data)
    }
    return ''
  }

  onFilter() {
    if (!this.offersFilter.some((el) => this.filterForm.get('offerId')?.value === el.value)) {
      this.filterForm.get('offerId')?.setValue('')
    }
    if (!this.exportersFilter.some((el) => this.filterForm.get('exporter')?.value === el.value)) {
      this.filterForm.get('exporter')?.setValue('')
    }
    if (!this.speciesFilter.some((el) => this.filterForm.get('specie')?.value === el.value)) {
      this.filterForm.get('specie')?.setValue('')
    }
    if (!this.varietiesFilter.some((el) => this.filterForm.get('variety')?.value === el.value)) {
      this.filterForm.get('variety')?.setValue('')
    }

    this.filteredExportOffers = this.exportOffers.filter(item => this.applyFilters(item));
    this.clearSelectFilters()
    this.filteredExportOffers.forEach(x => {
      this.setFilters(x)
    })
    this.sortFiters()

    if (this.dataTableComponent) {
      this.dataTableComponent.first = 0;
    }
  }

  applyFilters(item: ExportOfferInformation): boolean {
    if (typeof this.filterForm.get('offerId')?.value === 'number') {
      if (this.filterForm.get('offerId')?.value !== item.exportOffer.id) {
        return false;
      }
    }

    if (this.filterForm.get('exporter')?.value) {
      if (this.filterForm.get('exporter')?.value !== item.exportOffer.exporter.contactName) {
        return false;
      }
    }

    if (this.filterForm.get('specie')?.value) {
      if (this.filterForm.get('specie')?.value !== this.getSpeciesName(item.exportOffer.variety.species)) {
        return false;
      }
    }

    if (this.filterForm.get('variety')?.value) {
      if (this.filterForm.get('variety')?.value !== item.exportOffer.variety.name) {
        return false;
      }
    }

    if (this.filterForm.get('loadDate')?.value !== '' && this.filterForm.get('loadDate')?.value !== null) {
      if (moment(this.filterForm.get('loadDate')?.value).format("DD-MM-YYYY") !== moment(item.exportOffer.created).format("DD-MM-YYYY")) {
        return false;
      }
    }

    if (this.searchTerm.trim()) {
      const isOffer = this.compare(item.exportOffer.id);
      const isExporter = this.compare(item.exportOffer.exporter.contactName);
      const isSpeciesName = this.compare(item.exportOffer.variety.species, 'specie');
      const isVariety = this.compare(item.exportOffer.variety.name);
      const isCreatedDate = this.compare(item.exportOffer.created, 'date');

      return isOffer || isExporter || isSpeciesName || isVariety || isCreatedDate
    }

    return true;
  }

  compare(value: any, dataType?: string): boolean {
    let isFound = false;
    if (dataType === 'date') {
      isFound = moment(value).format("DD-MM-YYYY").toString().toLocaleLowerCase().includes(this.searchTerm.toLowerCase())
    }
    else if (dataType === 'specie') {
      isFound = this.getSpeciesName(value)?.toString().toLocaleLowerCase().includes(this.searchTerm.toLowerCase())
    }
    else {
      isFound = value.toString().toLocaleLowerCase().includes(this.searchTerm.toLowerCase())
    }

    return value?.toString() ? isFound : false
  }

  setFilters(item: ExportOfferInformation) {
    if (item.exportOffer.id !== undefined && !this.offersArray.includes(item.exportOffer.id)) {
      this.offersArray.push(item.exportOffer.id)
      this.offersFilter.push({value: item.exportOffer.id, label: item.exportOffer.id.toString()})
    }
    if (item.exportOffer.exporter.contactName && !this.exportersArray.includes(item.exportOffer.exporter.contactName)) {
      this.exportersArray.push(item.exportOffer.exporter.contactName)
      this.exportersFilter.push({value: item.exportOffer.exporter.contactName, label: item.exportOffer.exporter.contactName})
    }
    const specieName = this.getSpeciesName(item.exportOffer.variety.species)
    if (specieName && !this.speciesArray.includes(specieName)) {
      this.speciesArray.push(specieName)
      this.speciesFilter.push({value: specieName, label: specieName})
    }
    if (item.exportOffer.variety.name && !this.varietiesArray.includes(item.exportOffer.variety.name)) {
      this.varietiesArray.push(item.exportOffer.variety.name)
      this.varietiesFilter.push({value: item.exportOffer.variety.name, label: item.exportOffer.variety.name})
    }
  }

  sortFiters() {
    this.offersFilter.sort((a, b) => a.value - b.value)
    this.exportersFilter.sort((a, b) => a.value.localeCompare(b.value))
    this.speciesFilter.sort((a, b) => a.value.localeCompare(b.value))
    this.varietiesFilter.sort((a, b) => a.value.localeCompare(b.value))
  }

  clearSelectFilters() {
    this.offersArray = []
    this.exportersArray = []
    this.speciesArray = []
    this.varietiesArray = []

    this.offersFilter = []
    this.exportersFilter = []
    this.speciesFilter = []
    this.varietiesFilter = []
  }
}
